import { ANNOTATION_EXCERPT_PNG_MAX_BASE64_LENGTH } from "@rakazo/contracts";
import type { RelativeRect } from "./pdf-render";

/*
 * RAK-168: "Umsetzen" sends the bot a picture of each marked area. The picture is cut from the
 * canvas the page is already drawn on (no second render), as PNG, and shrunk step by step until
 * its base64 fits the contract limit. Pure size logic lives in fitExcerpt so it can be tested
 * without a real canvas.
 */

/** Longest side of an excerpt before any size fitting, in pixels; larger cuts are scaled down. */
export const EXCERPT_MAX_SIDE_PX = 1600;

/** How often fitExcerpt shrinks before it gives up. */
export const EXCERPT_MAX_TRIES = 6;

/** Encodes the excerpt at a scale (1 = full size) and returns its base64 without data-URL prefix. */
export type ExcerptEncoder = (scale: number) => string;

/**
 * Finds the largest scale whose PNG fits `maxLength` base64 characters: starts at `startScale`
 * and shrinks by the square root of the overshoot (PNG size grows roughly with the area) plus a
 * safety margin. Returns null when even the last try is too large.
 */
export function fitExcerpt(
  encode: ExcerptEncoder,
  startScale = 1,
  maxLength = ANNOTATION_EXCERPT_PNG_MAX_BASE64_LENGTH,
  maxTries = EXCERPT_MAX_TRIES,
): { base64: string; scale: number } | null {
  let scale = startScale;
  for (let attempt = 0; attempt < maxTries; attempt += 1) {
    const base64 = encode(scale);
    if (base64.length > 0 && base64.length <= maxLength) return { base64, scale };
    if (base64.length === 0) return null;
    scale *= Math.sqrt(maxLength / base64.length) * 0.9;
  }
  return null;
}

/** Pixel box of a page-relative rectangle on a canvas of the given backing size, at least 1×1. */
export function canvasCrop(rect: RelativeRect, canvasWidth: number, canvasHeight: number) {
  const sx = Math.floor(rect.x0 * canvasWidth);
  const sy = Math.floor(rect.y0 * canvasHeight);
  const sw = Math.max(1, Math.ceil(rect.x1 * canvasWidth) - sx);
  const sh = Math.max(1, Math.ceil(rect.y1 * canvasHeight) - sy);
  return { sx, sy, sw, sh };
}

const PNG_PREFIX = "data:image/png;base64,";

/**
 * Cuts a page-relative rectangle out of a drawn page canvas as base64 PNG within the contract
 * limit, or null when the canvas cannot be read or the cut never fits.
 */
export function cropExcerptPng(
  source: HTMLCanvasElement,
  rect: RelativeRect,
  createCanvas: () => HTMLCanvasElement = () => document.createElement("canvas"),
): string | null {
  const { sx, sy, sw, sh } = canvasCrop(rect, source.width, source.height);
  const target = createCanvas();
  const encode: ExcerptEncoder = (scale) => {
    target.width = Math.max(1, Math.round(sw * scale));
    target.height = Math.max(1, Math.round(sh * scale));
    const ctx = target.getContext("2d");
    if (!ctx) return "";
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, target.width, target.height);
    ctx.drawImage(source, sx, sy, sw, sh, 0, 0, target.width, target.height);
    const url = target.toDataURL("image/png");
    return url.startsWith(PNG_PREFIX) ? url.slice(PNG_PREFIX.length) : "";
  };
  const startScale = Math.min(1, EXCERPT_MAX_SIDE_PX / Math.max(sw, sh));
  return fitExcerpt(encode, startScale)?.base64 ?? null;
}
